import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { RoleObject } from 'src/constants';
import { membersService, type MemberByIDResponse } from 'src/services/members.service';
import { useInsuranceStore } from 'src/store/insurance';
import { useApplicationsStore } from 'src/store/applications';
import Popup from '../Popup';

const MemberDetailNew = () => {
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();
  const [member, setMember] = useState<MemberByIDResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPdfOpen, setIsPdfOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [loadingViewPHQ, setLoadingViewPHQ] = useState<string | null>(null);
  const [loadingDownloadPHQ, setLoadingDownloadPHQ] = useState<string | null>(null);
  const [loadingDownloadContract, setLoadingDownloadContract] = useState<string | null>(null);
  const [loadingDownloadZip, setLoadingDownloadZip] = useState<string | null>(null);
  const { plans: allPlans } = useInsuranceStore();
  const { getPayloadPdfUrl, getContractPdfUrl, getContractZipUrl } = useApplicationsStore();

  useEffect(() => {
    const fetchMember = async () => {
      if (!memberId) return;
      
      setLoading(true);
      setError(null);
      try {
        const data = await membersService.getById(memberId);
        setMember(data);
      } catch (err) {
        console.error('Failed to load member:', err);
        setError('Failed to load member data');
      } finally {
        setLoading(false);
      }
    };

    fetchMember();
  }, [memberId]);

  const handleBackToList = () => {
    navigate('/members');
  };

  const triggerDownload = (url: string, filename: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke later to ensure download starts
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  const handleShowPHQ = async (applicationId: string) => {
    try {
      setLoadingViewPHQ(applicationId);
      const url = await getPayloadPdfUrl(applicationId);
      if (url) {
        setPdfUrl(url);
        setIsPdfOpen(true);
      }
    } catch (err) {
      console.error('Failed to load PHQ PDF:', err);
    } finally {
      setLoadingViewPHQ(null);
    }
  };

  const handleDownloadPHQ = async (applicationId: string) => {
    try {
      setLoadingDownloadPHQ(applicationId);
      const url = await getPayloadPdfUrl(applicationId);
      triggerDownload(url, `phq-${applicationId}.pdf`);
    } catch (err) {
      console.error('Failed to download PHQ PDF:', err);
    } finally {
      setLoadingDownloadPHQ(null);
    }
  };

  const handleDownloadContract = async (applicationId: string) => {
    try {
      setLoadingDownloadContract(applicationId);
      const url = await getContractPdfUrl(applicationId);
      triggerDownload(url, `contract-${applicationId}.pdf`);
    } catch (err) {
      console.error('Failed to download contract PDF:', err);
    } finally {
      setLoadingDownloadContract(null);
    }
  };

  const handleDownloadZip = async (applicationId: string) => {
    try {
      setLoadingDownloadZip(applicationId);
      const url = await getContractZipUrl(applicationId);
      triggerDownload(url, `contract-${applicationId}.zip`);
    } catch (err) {
      console.error('Failed to download contract ZIP:', err);
    } finally {
      setLoadingDownloadZip(null);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 h-full rounded-[32px] p-2 id-card-container overflow-y-auto max-lg:my-2.5 relative flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (error || !member) {
    return (
      <div className="flex-1 h-full rounded-[32px] p-2 id-card-container overflow-y-auto max-lg:my-2.5 relative flex items-center justify-center">
        <div className="text-lg text-red-600">{error || 'Member not found'}</div>
      </div>
    );
  }

  return (
    <div className="flex-1 h-full rounded-[32px] p-2 id-card-container overflow-y-auto max-lg:my-2.5 relative">
      {/* Header */}
      <div className="px-8 py-6 flex items-center justify-between max-md:p-2">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBackToList}
            className="p-2 hover:bg-gray-100 flex items-center rounded-full"
          >
            <img src="/icons/left.svg" alt="back" />
          </button>
          <h1 className="id-card-title font-medium max-lg:!text-2xl max-md:!text-xl">
            {member.firstName} {member.midName && `${member.midName} `}{member.lastName}
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-8 py-6 max-md:px-2">
        <div className="bg-white-overlay big-corner p-6 max-md:p-4">
          {/* Personal Information */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Personal Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-gray-600">Member ID:</span>
                <p className="font-medium">{member.id}</p>
              </div>
              
              <div>
                <span className="text-gray-600">Email:</span>
                <p className="font-medium">{member.email}</p>
              </div>
              
              <div>
                <span className="text-gray-600">Phone:</span>
                <p className="font-medium">{member.phone || 'N/A'}</p>
              </div>
              
              <div>
                <span className="text-gray-600">Group ID:</span>
                <p className="font-medium">{member.groupId}</p>
              </div>
              
              <div>
                <span className="text-gray-600">Role:</span>
                <p className="font-medium">{RoleObject[member.role as keyof typeof RoleObject]}</p>
              </div>
              
              <div>
                <span className="text-gray-600">Status:</span>
                <p className="font-medium">
                  <span className={`inline-block px-2 py-1 rounded text-sm ${
                    member.status === 1 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {member.status === 1 ? 'Active' : 'Inactive'}
                  </span>
                </p>
              </div>
              
              <div>
                <span className="text-gray-600">Created:</span>
                <p className="font-medium">{new Date(member.createdAt).toLocaleDateString()}</p>
              </div>
              
              <div>
                <span className="text-gray-600">Updated:</span>
                <p className="font-medium">{new Date(member.updatedAt).toLocaleDateString()}</p>
              </div>
            </div>
          </div>

          {/* Subscriptions */}
          {member.subscriptions && member.subscriptions.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Subscriptions</h2>
              <div className="space-y-4">
                {member.subscriptions.map((subscription) => {
                  // Calculate total price for this subscription
                  const totalPrice = subscription.plans.reduce((sum, plan) => {
                    const planData = allPlans.find((p) => p.id === plan.planId);
                    const priceData = planData?.prices.find((p) => p.id === plan.priceId);
                    return sum + (priceData?.price || 0);
                  }, 0);

                  return (
                  <div key={subscription.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-4 pb-3 border-b">
                      <div>
                        <h3 className="font-semibold text-lg">Subscription #{subscription.id}</h3>
                      </div>
                      <div className="text-right">
                        <span className="text-gray-600 text-sm">Total Price</span>
                        <p className="font-bold text-2xl text-green-600">${totalPrice.toFixed(2)}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                      <div>
                        <span className="text-gray-600 text-sm">Application ID:</span>
                        <p className="font-medium">{subscription.applicationId}</p>
                      </div>
                      <div>
                        <span className="text-gray-600 text-sm">Stripe ID:</span>
                        <p className="font-medium text-xs">{subscription.stripeId}</p>
                      </div>
                      <div>
                        <span className="text-gray-600 text-sm">Status:</span>
                        <p className="font-medium">
                          <span className={`inline-block px-2 py-1 rounded text-sm ${
                            subscription.status === 1 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {subscription.status === 1 ? 'Active' : 'Inactive'}
                          </span>
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-600 text-sm">Activated:</span>
                        <p className="font-medium text-sm">
                          {subscription.activatedAt ? new Date(subscription.activatedAt).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-600 text-sm">Canceled:</span>
                        <p className="font-medium text-sm">
                          {subscription.canceledAt ? new Date(subscription.canceledAt).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                    </div>

                    {/* Plans within Subscription */}
                    {subscription.plans && subscription.plans.length > 0 && (
                      <div className="mt-4">
                        <h3 className="font-semibold mb-2">Plans</h3>
                        <div className="space-y-3">
                          {subscription.plans.map((plan) => {
                            const planData = allPlans.find((p) => p.id === plan.planId);
                            const priceData = planData?.prices.find((p) => p.id === plan.priceId);
                            
                            return (
                              <div key={plan.id} className="bg-gray-50 rounded p-3">
                                <div className="mb-3">
                                  <h4 className="font-bold text-base">{planData?.name || `Plan #${plan.planId}`}</h4>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                                  <div>
                                    <span className="text-gray-600">Price:</span>
                                    <p className="font-medium text-lg text-green-600">
                                      ${priceData?.price || 0}
                                      {priceData?.tier && ` (${priceData.tier})`}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">Coverage:</span>
                                    <p className="font-medium">
                                      ${priceData?.coverage ? priceData.coverage.toLocaleString() : 'N/A'}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">Status:</span>
                                    <p className="font-medium">
                                      <span className={`inline-block px-2 py-1 rounded text-xs ${
                                        plan.status === 1 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                      }`}>
                                        {plan.status === 1 ? 'Active' : 'Inactive'}
                                      </span>
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">Start Date:</span>
                                    <p className="font-medium">{new Date(plan.startAt).toLocaleDateString()}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">End Date:</span>
                                    <p className="font-medium">{new Date(plan.endAt).toLocaleDateString()}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">Applicants:</span>
                                    <p className="font-medium">{plan.applicants.length}</p>
                                  </div>
                                </div>

                                {/* Applicants */}
                                {plan.applicants && plan.applicants.length > 0 && (
                                  <div className="mt-3 pt-3 border-t">
                                    <h4 className="font-medium text-sm mb-2">Applicants:</h4>
                                    {plan.applicants.map((applicant, idx) => (
                                      <div key={idx} className="text-sm mb-2">
                                        <p>
                                          <strong>{applicant.firstName} {applicant.lastName}</strong> - 
                                          {applicant.gender}, DOB: {applicant.dob}, 
                                          Height: {applicant.heightFt}'{applicant.heightIn}", 
                                          Weight: {applicant.weight} lbs
                                          {applicant.nicotine && ', Nicotine user'}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Applications */}
          {member.applications && member.applications.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold mb-4">Applications</h2>
              <div className="space-y-4">
                {member.applications.map((application) => (
                  <div key={application.id} className="border rounded-lg p-4">
                    <div className="mb-4 pb-3 border-b">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold text-lg">Application Details</h3>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleShowPHQ(application.id)}
                            disabled={loadingViewPHQ === application.id}
                            className={`px-3 py-1 text-sm border rounded-lg flex items-center gap-1 ${
                              loadingViewPHQ === application.id
                                ? 'opacity-50 cursor-not-allowed border-gray-300'
                                : 'border-blue-300 text-blue-600 hover:bg-blue-50'
                            }`}
                            title="View PHQ PDF"
                          >
                            {loadingViewPHQ === application.id ? (
                              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                              </svg>
                            ) : (
                              <img src="/icons/pdf.svg" alt="view" className="w-4 h-4" />
                            )}
                            View PHQ
                          </button>
                          <button
                            onClick={() => handleDownloadPHQ(application.id)}
                            disabled={loadingDownloadPHQ === application.id}
                            className={`px-3 py-1 text-sm border rounded-lg flex items-center gap-1 ${
                              loadingDownloadPHQ === application.id
                                ? 'opacity-50 cursor-not-allowed border-gray-300'
                                : 'border-green-300 text-green-600 hover:bg-green-50'
                            }`}
                            title="Download PHQ PDF"
                          >
                            {loadingDownloadPHQ === application.id ? (
                              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                              </svg>
                            ) : (
                              <img src="/icons/download-icn.svg" alt="download" className="w-4 h-4" />
                            )}
                            Download PHQ
                          </button>
                          <button
                            onClick={() => handleDownloadContract(application.id)}
                            disabled={loadingDownloadContract === application.id}
                            className={`px-3 py-1 text-sm border rounded-lg flex items-center gap-1 ${
                              loadingDownloadContract === application.id
                                ? 'opacity-50 cursor-not-allowed border-gray-300'
                                : 'border-purple-300 text-purple-600 hover:bg-purple-50'
                            }`}
                            title="Download Contract PDF"
                          >
                            {loadingDownloadContract === application.id ? (
                              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                              </svg>
                            ) : (
                              <img src="/icons/pdf.svg" alt="contract" className="w-4 h-4" />
                            )}
                            Contract PDF
                          </button>
                          <button
                            onClick={() => handleDownloadZip(application.id)}
                            disabled={loadingDownloadZip === application.id}
                            className={`px-3 py-1 text-sm border rounded-lg flex items-center gap-1 ${
                              loadingDownloadZip === application.id
                                ? 'opacity-50 cursor-not-allowed border-gray-300'
                                : 'border-orange-300 text-orange-600 hover:bg-orange-50'
                            }`}
                            title="Download Contract ZIP"
                          >
                            {loadingDownloadZip === application.id ? (
                              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                              </svg>
                            ) : (
                              <img src="/icons/download-icn.svg" alt="zip" className="w-4 h-4" />
                            )}
                            Contract ZIP
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-600">Application ID:</span>
                          <p className="font-medium text-xs">{application.id}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Created:</span>
                          <p className="font-medium">{new Date(application.createdAt).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Updated:</span>
                          <p className="font-medium">{new Date(application.updatedAt).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Income:</span>
                          <p className="font-medium">${application.payload.income}</p>
                        </div>
                      </div>
                    </div>

                    {/* Applicants in Application */}
                    {application.payload.applicants && application.payload.applicants.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-semibold mb-2">Applicants</h4>
                        <div className="space-y-2">
                          {application.payload.applicants.map((applicant, idx) => (
                            <div key={idx} className="bg-gray-50 rounded p-3 text-sm">
                              <p className="font-medium mb-1">
                                {applicant.firstName} {applicant.midName && `${applicant.midName} `}{applicant.lastName}
                              </p>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-gray-600">
                                <div>Gender: {applicant.gender}</div>
                                <div>DOB: {applicant.dob}</div>
                                <div>Height: {applicant.heightFt}'{applicant.heightIn}"</div>
                                <div>Weight: {applicant.weight} lbs</div>
                                <div>Nicotine: {applicant.nicotine ? 'Yes' : 'No'}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Plans in Application */}
                    {application.payload.plans && application.payload.plans.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-semibold mb-2">Plans</h4>
                        <div className="space-y-2">
                          {application.payload.plans.map((plan, idx) => {
                            const planData = allPlans.find((p) => p.id === plan.id);
                            const priceData = planData?.prices.find((p) => p.id === plan.priceId);
                            
                            return (
                              <div key={idx} className="bg-gray-50 rounded p-3">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <p className="font-medium">{planData?.name || `Plan #${plan.id}`}</p>
                                    <p className="text-sm text-gray-600">
                                      Applicants: {plan.applicants.map(a => application.payload.applicants[a]?.firstName).join(', ')}
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <p className="font-bold text-green-600">${priceData?.price || 0}</p>
                                    {priceData?.tier && (
                                      <p className="text-xs text-gray-600">{priceData.tier}</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* PHQ Information */}
                    {application.payload.phq && (
                      <div>
                        <h4 className="font-semibold mb-2">Health Questionnaire (PHQ)</h4>
                        <div className="bg-gray-50 rounded p-3">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                            <div>
                              <span className="text-gray-600">Treatment:</span>
                              <p className="font-medium">{application.payload.phq.treatment ? 'Yes' : 'No'}</p>
                            </div>
                            <div>
                              <span className="text-gray-600">Invalid:</span>
                              <p className="font-medium">{application.payload.phq.invalid ? 'Yes' : 'No'}</p>
                            </div>
                            <div>
                              <span className="text-gray-600">Pregnancy:</span>
                              <p className="font-medium">{application.payload.phq.pregnancy ? 'Yes' : 'No'}</p>
                            </div>
                            <div>
                              <span className="text-gray-600">Disclaimer:</span>
                              <p className="font-medium">{application.payload.phq.disclaimer ? 'Agreed' : 'Not Agreed'}</p>
                            </div>
                            <div>
                              <span className="text-gray-600">Effective Date:</span>
                              <p className="font-medium">{application.payload.phq.effectiveDate}</p>
                            </div>
                            <div>
                              <span className="text-gray-600">Medications:</span>
                              <p className="font-medium">{application.payload.phq.medications.length}</p>
                            </div>
                            <div>
                              <span className="text-gray-600">Issues:</span>
                              <p className="font-medium">{application.payload.phq.issues.length}</p>
                            </div>
                            <div>
                              <span className="text-gray-600">Conditions:</span>
                              <p className="font-medium">{application.payload.phq.conditions.length}</p>
                            </div>
                          </div>
                          {application.payload.phq.signature && (
                            <div className="mt-3 pt-3 border-t">
                              <span className="text-gray-600 text-sm">Signature:</span>
                              <div className="mt-2 border rounded p-2 bg-white">
                                <img 
                                  src={application.payload.phq.signature} 
                                  alt="Signature" 
                                  className="max-h-20 object-contain"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <Popup isOpen={isPdfOpen} onClose={() => setIsPdfOpen(false)} fullscreen>
        {pdfUrl ? (
          <iframe src={pdfUrl} title="PHQ PDF" className="w-full h-full" />
        ) : null}
      </Popup>
    </div>
  );
};

export default MemberDetailNew;

